import type { HandleSide, WidthBounds } from './index.types';

/** Pixel step applied by ArrowLeft and ArrowRight. */
export const KEYBOARD_RESIZE_STEP = 10;

/**
 * Clamps a width between the given bounds and rounds to whole pixels.
 * Mouse clientX can be fractional; rounding keeps aria-valuenow and storage integers.
 */
export const clampWidth = (width: number, bounds: WidthBounds): number =>
  Math.round(Math.min(bounds.max, Math.max(bounds.min, width)));

/**
 * Parses stored width from localStorage.
 */
export const parseStoredWidth = (
  raw: string | null,
  bounds: WidthBounds
): number => {
  const fallback = clampWidth(bounds.default, bounds);

  if (raw === null) {
    return fallback;
  }

  const parsed = Number(raw);

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return clampWidth(parsed, bounds);
};

/**
 * Computes the next width from a keyboard key, respecting handle side.
 * Arrow keys nudge by KEYBOARD_RESIZE_STEP; Home → min; End → max.
 * Refer to https://www.w3.org/WAI/ARIA/apg/patterns/windowsplitter/.
 */
export const getWidthFromKeyboardKey = ({
  key,
  currentWidth,
  bounds,
  handleSide,
  step = KEYBOARD_RESIZE_STEP,
}: {
  key: string;
  currentWidth: number;
  bounds: WidthBounds;
  handleSide: HandleSide;
  step?: number;
}): number | null => {
  const grow = handleSide === 'right' ? step : -step;
  const shrink = -grow;

  switch (key) {
    case 'ArrowRight':
      return clampWidth(currentWidth + grow, bounds);
    case 'ArrowLeft':
      return clampWidth(currentWidth + shrink, bounds);
    case 'Home':
      return bounds.min;
    case 'End':
      return bounds.max;
    default:
      return null;
  }
};

/**
 * Reads stored width from localStorage.
 */
export const readStoredWidth = (
  storageKey: string,
  bounds: WidthBounds
): number => {
  try {
    return parseStoredWidth(localStorage.getItem(storageKey), bounds);
  } catch (error) {
    console.error('Failed to read resizable width from localStorage:', error);
    return clampWidth(bounds.default, bounds);
  }
};

/**
 * Writes stored width to localStorage.
 */
export const writeStoredWidth = (storageKey: string, width: number): void => {
  try {
    localStorage.setItem(storageKey, String(width));
  } catch (error) {
    console.error('Failed to save resizable width to localStorage:', error);
  }
};
